import styled from "styled-components";
import { Link, useNavigate } from "react-router-dom";
import { Link as LinkComponent } from "../../components/atoms/Link";
import colours from "../../lib/colours";
import { ReactComponent as ArrowIcon } from "../../assets/svg/arrow-right-long.svg";
import Footer from "../Other/Footer";
import appConfig from "../../lib/appConfig";
import CompactTable, {
  ITableRowData,
} from "../../components/organisms/CompactTable";
import { ExternalLinkIcon } from "../../components/atoms/ExternalLinkIcon";
import img7 from "../../assets/images/Grass7.jpg";
import img5 from "../../assets/images/Grass5.jpg";
import img4 from "../../assets/images/Grass4.jpg";
import img1 from "../../assets/images/Grass1.jpg";
import FilledButton from "../../components/atoms/FilledButton";
import { ReactComponent as ArrowRightIcon } from "../../assets/svg/arrow-right.svg";
import { guidelines } from "../../lib/constants";

const Container = styled.div`
  height: calc(100vh - ${appConfig.headerHeight}px);
  display: flex;
  flex-direction: column;
  font-weight: 300;
  color: ${colours.darkerGrey};
`;

const SubHeader = styled.div`
  background-color: ${colours.th1l1alt};
  color: ${colours.white};
`;

const FluidContainer = styled.div`
  max-width: ${appConfig.homePageContentWidth};
  margin: auto;
  padding-inline: 1rem;
`;

const SubHeaderInnerContainer = styled(FluidContainer)`
  padding-block: 1rem;
`;

const SubHeaderLinkContainer = styled.div`
  height: 69px;
  box-sizing: border-box;
  padding-top: 8px;
`;

const SubHeaderLinkInnerContainer = styled.div`
  display: flex;
  align-items: center;
`;

const BackLink = styled(Link)`
  padding: 2px 0;
  font-weight: 400;
  font-size: 14px;
  color: ${colours.white};
  display: inline;
  text-decoration: none;
  &: hover {
    text-decoration: underline;
  }
`;

const StyledBackArrow = styled(ArrowIcon)`
  position: relative;
  top: -2px;
  width: 1.6875rem;
  height: 0.875rem;
  transform: rotate(180deg);
  margin-right: 12px;
`;

const SubHeaderText = styled.h2`
  font-size: 2.1875rem;
  font-weight: 500;
  margin-block: -2px 4px;
`;

const MainContent = styled.div`
  flex: 1;
`;

const MainContentInner = styled(FluidContainer)`
  margin-block: 1.8rem;
  padding-bottom: 80px;
`;

const Header3 = styled.h3`
  font-size: 28px;
  font-weight: 600;
  color: ${colours.th1};
  margin-block: 60px 20px;
`;

const MediumSpan = styled.span`
  font-weight: 500;
`;

const TableContainer = styled.div`
  margin-bottom: 8px;
`;

const ImagesContainer = styled.div`
  margin-block: 30px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  grid-gap: 16px;
`;

const TileImg = styled.img`
  max-height: 400px;
  width: 100%;
  object-fit: contain;
`;

const ActionButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-block: 48px 36px;
`;

const ActionButton = styled(FilledButton)`
  display: flex;
  align-items: center;
  height: auto;
  padding: 14px 40px;
  min-width: min(80vw, 386px);
  justify-content: center;
  padding-block: 24px;
  width: min(500px, 100%);
  font-size: 18px;
  transition: background-color 0.6s;
`;

const ButtonArrowIcon = styled(ArrowRightIcon)`
  height: 14px;
  margin-left: 10px;
`;

const centeredCellStyle = {
  textAlign: "center",
};

const loc1Style = {
  backgroundColor: colours.location1,
  color: colours.location1Text,
  fontWeight: 600,
  ...centeredCellStyle,
};

const loc2Style = {
  backgroundColor: colours.location2,
  color: colours.location2Text,
  fontWeight: 600,
  ...centeredCellStyle,
};

const loc3Style = {
  backgroundColor: colours.location3,
  color: colours.location3Text,
  fontWeight: 600,
  ...centeredCellStyle,
};

const headers: string[] = [
  "Extent of native vegetation",
  "Location 1",
  "Location 2",
  "Location 3",
];

const headerStyles: any[] = [
  { backgroundColor: colours.grey1 },
  loc1Style,
  loc2Style,
  loc3Style,
];

const rowStyles = [
  null,
  centeredCellStyle,
  centeredCellStyle,
  centeredCellStyle,
];

const rows: ITableRowData[] = [
  {
    rowData: [
      "Less than 0.5 hectares and not including any Large Trees",
      "Basic",
      "Intermediate",
      "Detailed",
    ],
    rowStyles,
  },
  {
    rowData: [
      "Less than 0.5 hectares and including one or more Large Trees",
      "Intermediate",
      "Intermediate",
      "Detailed",
    ],
    rowStyles,
  },
  {
    rowData: ["0.5 hectares or more", "Detailed", "Detailed", "Detailed"],
    rowStyles,
  },
];

function RemovalHome() {
  const navigate = useNavigate();

  return (
    <Container>
      <SubHeader>
        <SubHeaderInnerContainer>
          <SubHeaderLinkContainer>
            <SubHeaderLinkInnerContainer>
              <StyledBackArrow />
              <BackLink to="/">NVR Map</BackLink>
            </SubHeaderLinkInnerContainer>
          </SubHeaderLinkContainer>
          <SubHeaderText>
            Applications to remove native vegetation
          </SubHeaderText>
        </SubHeaderInnerContainer>
      </SubHeader>
      <MainContent>
        <MainContentInner>
          <Header3>Native Vegetation Removal Tool</Header3>
          <p>
            This tool is used to generate a Native Vegetation Removal Report, to
            support an application for approval to remove, destroy or lop native
            vegetation.
          </p>
          <p>
            The location and extent of your proposed removal will determine
            whether the <MediumSpan>Basic</MediumSpan>,{" "}
            <MediumSpan>Intermediate</MediumSpan> or{" "}
            <MediumSpan>Detailed Assessment Pathway</MediumSpan> applies.
          </p>
          <TableContainer>
            <CompactTable
              headers={headers}
              headerStyles={headerStyles}
              rows={rows}
              getRowKey={(row: any) => row[0]}
            />
          </TableContainer>
          <p>
            The{" "}
            <LinkComponent
              href="User-guide.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              NVR Map User Guide
            </LinkComponent>
            <ExternalLinkIcon /> provides instructions for identifying proposed
            removals using the Draw, Simple Upload or Detailed Upload functions.
          </p>
          <p>
            For proposals falling under the Basic or Intermediate Assessment
            Pathway, offset calculations rely on modelled condition scores when
            removal features are defined using the Draw or Simple Upload
            functions. Alternatively, a Detailed Upload, incorporating data from
            a certified native vegetation assessor, may be completed if
            site-assessed data is preferred.
          </p>
          <p>
            If the Detailed Assessment Pathway applies, you are required to
            engage a certified native vegetation assessor to complete a site
            assessment and generate a report using the Detailed Upload function.
          </p>
          <Header3>Information Requirements</Header3>
          <p>
            Clearing proposals in the Basic or Intermediate Assessment Pathway
            are required to address the application requirements outlined in
            Table 4 of the{" "}
            <LinkComponent
              href={guidelines}
              target="_blank"
              title="Guidelines for the removal, destruction or lopping of native vegetation"
              rel="noopener noreferrer"
            >
              Guidelines for the removal, destruction or lopping of native
              vegetation
            </LinkComponent>
            <ExternalLinkIcon /> (the Guidelines). For proposals in the Detailed
            Assessment Pathway, additional information requirements are outlined
            in Table 5.
          </p>
          <p>
            Before you use this tool,{" "}
            <MediumSpan>you need to collect site information</MediumSpan>.
            Guidance provided in this tool will assist you in defining and
            mapping Patches, Patch Trees and Scattered Trees.
          </p>
          <p>
            <MediumSpan>
              The correct mapping of native vegetation will ensure the accurate
              calculation of any offset requirements.
            </MediumSpan>
          </p>
          <p>
            <MediumSpan>
              Native vegetation can be difficult to identify, particularly areas
              of native grassland.
            </MediumSpan>{" "}
            All the images below depict Patches of native grassland, which would
            require approval to remove.
          </p>
          <ImagesContainer>
            <TileImg src={img7} alt="Grassland patch example 1" />
            <TileImg src={img5} alt="Grassland patch example 2" />
            <TileImg src={img4} alt="Grassland patch example 3" />
            <TileImg src={img1} alt="Grassland patch example 4" />
          </ImagesContainer>
          <p>
            If you are unsure whether the vegetation you want to remove is
            native, you should seek advice from your local Council.
          </p>
          <Header3>Next Steps</Header3>
          <p>
            This tool will guide you through the process of generating a Native
            Vegetation Removal Report. Please click on the button below to
            proceed.
          </p>
          <ActionButtonContainer>
            <ActionButton onClick={() => navigate("/map/removal")}>
              <span>Launch tool</span>
              <ButtonArrowIcon />
            </ActionButton>
          </ActionButtonContainer>
        </MainContentInner>
      </MainContent>
      <Footer />
    </Container>
  );
}

export default RemovalHome;
